import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Compass,
  Library,
  Route,
  Zap,
  Users,
  BookOpen,
  Award,
  Bot,
  CalendarDays,
  User,
  LayoutGrid,
  LifeBuoy,
  type LucideIcon,
} from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo.js';
import { NAV_GROUPS, NAV_LABELS } from '@/lib/nav-labels.js';
import { podeVer, useMe, type AreaRestrita } from '@/lib/me.js';

type NavItem = { label: string; to: string; icon: LucideIcon; end?: boolean; area?: AreaRestrita };
type NavGroup = { label: string; items: NavItem[] };

const homeItem: NavItem = { label: NAV_LABELS.inicio, to: '/inicio', icon: LayoutDashboard, end: true };

const groups: NavGroup[] = [
  {
    label: NAV_GROUPS.metodo,
    items: [
      { label: NAV_LABELS.diagnostico5Forcas, to: '/metodo/diagnostico', icon: Compass },
      { label: NAV_LABELS.biblioteca5Forcas, to: '/metodo/biblioteca', icon: Library },
    ],
  },
  {
    label: NAV_GROUPS.conhecimento,
    items: [
      { label: NAV_LABELS.jornadas, to: '/conhecimento/jornadas', icon: Route, area: 'trilhas' },
      { label: NAV_LABELS.microLearning, to: '/conhecimento/micro', icon: Zap, area: 'trilhas' },
      { label: NAV_LABELS.eventos, to: '/eventos', icon: CalendarDays },
    ],
  },
  {
    label: NAV_GROUPS.direcao,
    items: [
      { label: NAV_LABELS.conselheiro, to: '/direcao/conselheiro', icon: Users, area: 'conselheiro' },
      { label: NAV_LABELS.bibliotecaDoMetodo, to: '/direcao/biblioteca-do-metodo', icon: BookOpen, area: 'formacao' },
      { label: NAV_LABELS.certificacao, to: '/direcao/certificacao', icon: Award, area: 'formacao' },
    ],
  },
  {
    label: NAV_GROUPS.tecnologia,
    items: [{ label: NAV_LABELS.hackIa, to: '/tecnologia/hack-ia', icon: Bot, area: 'ia' }],
  },
  {
    label: NAV_GROUPS.conexao,
    items: [{ label: NAV_LABELS.solucoesHack, to: '/conexao/ecossistema', icon: LayoutGrid }],
  },
  {
    label: NAV_GROUPS.sobreMim,
    items: [
      { label: 'Perfil', to: '/conta', icon: User },
      { label: 'Ajuda', to: '/ajuda', icon: LifeBuoy },
    ],
  },
];

function SidebarLink({ item, end }: { item: NavItem; end?: boolean }) {
  const Icon = item.icon;
  return (
    <NavLink to={item.to} end={end ?? item.end} style={{ textDecoration: 'none' }}>
      {({ isActive }) => (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 12px',
            marginBottom: 2,
            borderLeft: isActive ? '2px solid var(--primary)' : '2px solid transparent',
            background: isActive ? 'var(--primary-soft)' : 'transparent',
            color: isActive ? 'var(--nav-ativo)' : 'var(--text2)',
            fontWeight: isActive ? 600 : 400,
            fontSize: 13,
          }}
        >
          <Icon size={18} strokeWidth={1.5} />
          {item.label}
        </span>
      )}
    </NavLink>
  );
}

export function AppSidebar() {
  const me = useMe();
  // Enquanto o /me não chega, o que é restrito fica de fora: some depois de
  // aparecer é pior do que aparecer depois.
  const visiveis = groups
    .map((g) => ({ ...g, items: g.items.filter((i) => !i.area || podeVer(me.data, i.area)) }))
    .filter((g) => g.items.length > 0);
  return (
    <aside
      className="fixed inset-y-0 left-0 z-40 hidden w-[240px] flex-col md:flex"
      style={{ background: 'var(--bg2)', borderRight: '1px solid var(--border)' }}
    >
      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <BrandLogo height={28} linkTo="/inicio" />
      </div>

      <nav style={{ flex: 1, padding: '12px 8px 16px', overflowY: 'auto' }}>
        <SidebarLink item={homeItem} end />

        {visiveis.map((group) => (
          <div key={group.label} style={{ marginBottom: 16 }}>
            <div
              style={{
                padding: '4px 12px 8px',
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.06em',
                color: 'var(--text3)',
              }}
            >
              {group.label}
            </div>
            {group.items.map((item) => (
              <SidebarLink key={item.to} item={item} />
            ))}
          </div>
        ))}
      </nav>
    </aside>
  );
}
